// src/pages/.../ReglementArbitragePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import {
  getReglementsArbitrage,
  deleteReglementArbitrage,
} from "../../services/reglementarbitrageServices";
import ReglementCard from "./ReglementCard";
import Button from "../../components/Button/Button";
import CreateReglementArbitrageModal from "./CreateReglementArbitrageModal";
import EditReglementArbitrageModal from "./EditReglementArbitrageModal";
import InfoBubble from "../../components/InfoBubble/InfoBubble";
import PageHeader from "../../components/Layout/PageHeader";
import { ToastContainer, toast } from "react-toastify";

const ReglementArbitragePage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [reglements, setReglements] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentReglement, setCurrentReglement] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [onlyWithLink, setOnlyWithLink] = useState(false);

  const canManage =
    userRole === "Administrateur" || userRole === "Bureau";

  const fetchReglements = async () => {
    try {
      setLoading(true);
      const data = await getReglementsArbitrage();
      setReglements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des règlements :", error);
      toast.error("Erreur lors du chargement des règlements.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReglements();
  }, []);

  const filteredReglements = useMemo(() => {
    let list = reglements.slice();

    if (onlyWithLink) {
      list = list.filter((r) => r.lien_url);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const title = (r.title || "").toLowerCase();
        const content = (r.content || "").toLowerCase();
        return title.includes(q) || content.includes(q);
      });
    }

    // tri : plus récents en haut si dates dispo, sinon alpha
    list.sort((a, b) => {
      const da = new Date(a.updated_at || a.created_at || 0);
      const db = new Date(b.updated_at || b.created_at || 0);
      const va = isNaN(da) ? 0 : da.getTime();
      const vb = isNaN(db) ? 0 : db.getTime();
      if (va !== vb) return vb - va; // récents d'abord
      return (a.title || "").localeCompare(b.title || "", "fr", {
        sensitivity: "base",
      });
    });

    return list;
  }, [reglements, onlyWithLink, searchQuery]);

  const handleAddReglementClick = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditReglement = (reglement) => {
    setCurrentReglement(reglement);
    setIsEditModalOpen(true);
  };

  const handleDeleteReglement = async (id) => {
    try {
      await deleteReglementArbitrage(id);
      setReglements((prev) => prev.filter((r) => r.id !== id));
      toast.success("Règlement supprimé avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression du règlement:", error);
      toast.error("Erreur lors de la suppression du règlement");
    }
  };

  const closeCreateModal = () => setIsCreateModalOpen(false);

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentReglement(null);
  };

  const refreshReglementsList = async () => {
    await fetchReglements();
  };

  const handleCreateSuccess = () => {
    closeCreateModal();
    refreshReglementsList();
    toast.success("Règlement créé avec succès");
  };

  const handleEditSuccess = () => {
    closeEditModal();
    refreshReglementsList();
    toast.success("Règlement modifié avec succès");
  };

  return (
    <div className="flex flex-col w-full mx-auto min-h-screen mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] px-2 pb-10 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50">
      <ScrollIndicator />

      <PageHeader
        eyebrow="Arbitres & commissaires sportifs"
        title="Règlements d’arbitrage"
        subtitle="Retrouvez ici les textes de référence pour la saison : règlements officiels IJF et fédéraux, protocoles de table, consignes départementales…"
        align="center"
        size="md"
      />

      <div className="w-full max-w-6xl mx-auto space-y-4">
        <InfoBubble title="Consultation des règlements">
          <p>
            Les documents ci-dessous sont mis à disposition des arbitres et
            commissaires sportifs pour préparer les compétitions, les
            formations et les examens. Vous pouvez filtrer par mots-clés et
            accéder aux documents joints (PDF, pages externes…).
          </p>
        </InfoBubble>

        {/* Barre d’actions / filtres + bouton admin */}
        <div className="rounded-2xl bg-white/90 dark:bg-slate-900/90 border border-slate-200/70 dark:border-slate-700/70 shadow-sm px-4 py-3 sm:px-5 sm:py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Rechercher
              </label>
              <input
                type="text"
                placeholder="Titre, mots-clés, contenu…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
              />
            </div>

            <button
              type="button"
              onClick={() => setOnlyWithLink((v) => !v)}
              className={`inline-flex items-center justify-center rounded-full border px-3 py-1.5 text-xs sm:text-[0.8rem] transition ${
                onlyWithLink
                  ? "bg-primary text-white border-primary"
                  : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"
              }`}
            >
              {onlyWithLink
                ? "Uniquement avec document joint"
                : "Inclure les textes sans document joint"}
            </button>
          </div>

          {canManage && (
            <div className="flex justify-end">
              <Button
                text="Ajouter un règlement"
                onClick={handleAddReglementClick}
                className="!rounded-xl !px-4 !py-2 text-sm"
              />
            </div>
          )}
        </div>

        {/* Liste des règlements */}
        <div className="mt-2">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-300">
              Chargement des règlements…
            </div>
          ) : filteredReglements.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-300">
              Aucun règlement ne correspond à vos critères.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredReglements.map((reglement) => (
                <ReglementCard
                  key={reglement.id}
                  reglement={reglement}
                  onEdit={() => handleEditReglement(reglement)}
                  onDelete={() => handleDeleteReglement(reglement.id)}
                  canModify={canManage}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateReglementArbitrageModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        onCreateSuccess={handleCreateSuccess}
      />
      <EditReglementArbitrageModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onEditSuccess={handleEditSuccess}
        reglement={currentReglement}
      />
      <ToastContainer />
    </div>
  );
};

export default ReglementArbitragePage;
